import {
    BuildData,
    Modifier, ModifierMap, PatchInfo,
    Spec, SpecMap,
    Spell, SpellMap, Talent,
    TalentMap
} from 'web/data/models'
import { CooldownResolver } from 'web/engine/cooldowns/core-resolver'
import { assertDefined } from 'web/utils/utils'
import { FIGHT_CATEGORY_ID } from './general/id'
import { Build_1001_1 } from './patches/10.0.1/build_1001_1/build'
import { Build_830_1 } from './patches/8.3.0/build_830_1/build'
import { Build_901_2 } from './patches/9.0.1/build_901_2/build'
import { Build_910_1 } from './patches/9.1.0/build_910_1/build'
import { Build_920_1 } from './patches/9.2.0/build_920_1/build'
import { Build_1 } from './patches/mock/build_1/build'
import { Build_1030_0 } from './patches/10.3.0/build_1030_0/build'
import { Build_1100_1 } from './patches/11.0.0/build_1100_1/build'
import { Build_1110_1 } from './patches/11.1.0/build_1110_1/build'

/**
 * Latest build is always at the top
 */
const BUILDS = [
    Build_1110_1,
    Build_1100_1,
    Build_1030_0,
    Build_1001_1,
    Build_920_1,
    Build_910_1,
    Build_901_2,
    Build_830_1,
    Build_1,
]

/**
 * Returns the latest available build. Take care never to use this in places
 * where we need to be backwards compatible.
 */
export function getCurrentBuild(): BuildData {
    return BUILDS[0]
}

export function getBuildByPatchInfo(patchInfo: PatchInfo): BuildData {
    return getBuildByVersion(patchInfo.buildVersion)
}

export function getBuildByVersion(buildVersion: string): BuildData {
    const build = BUILDS.find(b => b.patchInfo.buildVersion === buildVersion)
    assertDefined(build, buildVersion)

    return build
}

export function isPatchInfoCurrent(patchInfo: PatchInfo): boolean {
    return getCurrentBuild().patchInfo.buildVersion === patchInfo.buildVersion
}

export function isLegacyFightCategory(categoryId: string) {
    // TODO: This impl. isn't very good, you're going to forget adding
    // things here each new tier, it should be centralized next to the
    // other GUI data
    const legacyCategories: string[] = [
        ...Object.values(FIGHT_CATEGORY_ID.CN),
        ...Object.values(FIGHT_CATEGORY_ID.SOD),
        ...Object.values(FIGHT_CATEGORY_ID.SOFO),
        ...Object.values(FIGHT_CATEGORY_ID.VOTI),
        ...Object.values(FIGHT_CATEGORY_ID.ASC),
        ...Object.values(FIGHT_CATEGORY_ID.ADH),
        ...Object.values(FIGHT_CATEGORY_ID.NP),
    ]

    return legacyCategories.includes(categoryId)
}

/**
 * Builds a BuildData object from build-specific data.
 */
export function createBuildData(params: {

    buildVersion: string
    changelog?: string[]

    spells: SpellMap
    specs: SpecMap
    talents: TalentMap
    modifiers: ModifierMap

    __CooldownResolver: (spellId: string) => CooldownResolver

}): BuildData {

    return {

        patchInfo: { buildVersion: params.buildVersion },
        changelog: params.changelog,

        spellsById: params.spells,
        specsById: params.specs,
        talentsById: params.talents,
        modifiersById: params.modifiers,

        SpecByName: (classId: string, fullSpecName: string) => {
            const spec = Object
                .values(params.specs)
                .find(spec => {
                    return spec.classId === classId
                        && spec.name === fullSpecName
                })

            assertDefined(spec)

            return spec
        },

        Spec: (specId: string): Spec => {
            return params.specs[specId]
        },

        Spell: (spellId: string): Spell => {
            return params.spells[spellId]
        },

        Talent: (talentId: string): Talent => {
            return params.talents[talentId]
        },

        Modifier: (modId: string): Modifier => {
            return params.modifiers[modId]
        },

        __CooldownResolver: params.__CooldownResolver,
    }

}

/**
 * Takes all the props of the obj and returns their values as an array, if there
 * are any. Returns an empty array otherwise.
 *
 * Assumes clean string values, should be used to expand ID objects.
 */
export function propsToArray(obj: any): string[] {
    const values = Object.values(obj)

    if (values.length >= 1) {
        return values as string[]
    } else {
        return []
    }
}

export function copyTalentPropsToSpell(talent: Talent): {
    name: string
    shortName: string
    talentId: string | undefined
    game_id: number | undefined
    icon_name: string | undefined
} {
    return {
        name: talent.name,
        shortName: talent.shortName,
        game_id: talent.game_id,
        icon_name: talent.icon_name,
        talentId: talent.id
    }
}
